import type { ReactNode } from 'react'
import { ApiFailure, isForbiddenError } from '../app/errors'
import { Card } from './card'
import { t } from '../copy'
import { Page, PageHeader } from './page'
import { cn } from './cn'

/**
 * 加载中与出错。
 *
 * 加载态**一个字都不写**：设计稿里没有 loading 文案，而「正在加载…」这种话既是术语
 * 也是噪音。用几块跳动的灰底占位就够——它跑的是 nwpulse，与「正在进行」的状态点同一套语言。
 *
 * ## 骨架必须长得像即将出现的内容（R4 复审裁决）
 *
 * 全平台曾共用一个「仪表盘」骨架（4 张统计卡 + 一块大区），列表页、时间线页、
 * 设置页全用它——内容一出来布局就跳一次，用户读的是「换了页」而不是「填上了」。
 * 所以 ScreenLoading 按 `shape` 分形状，每个形状镜像对应页面的**顶层结构**
 * （栏数、行高、卡片比例），只灰掉内容本身。骨架不追求像素级一致，
 * 追求的是**换入真实内容时不发生结构性跳动**。
 */
export function ScreenLoading({
  className,
  shape = 'overview',
  width = 'wide',
  cols = 3,
  rows = 6,
  stats = 0,
  title,
}: {
  className?: string
  /** 骨架形状：镜像目标页的顶层结构，见上方注释。 */
  shape?: 'overview' | 'list' | 'cards' | 'timeline' | 'detail' | 'chat' | 'settings' | 'split'
  /** 页宽档位与 Page 一致；reading/narrow/workspace 的页别再用默认宽。 */
  width?: 'wide' | 'narrow' | 'reading' | 'workspace'
  /** cards 形状的列数（hosts 2 / today 3 / vendor 3）。 */
  cols?: number
  /** list 形状的行数。 */
  rows?: number
  /** list 形状顶上那条统计卡条的数量（模型库是 4）。 */
  stats?: number
  /** 页标题。给了就渲染真标题（标题是静态文案，不该闪一下灰块）。 */
  title?: ReactNode
}) {
  return (
    <Page className={className} width={width}>
      <div aria-busy="true" className="flex flex-col gap-npc-grid-lg">
        {title ? <PageHeader title={title} /> : <Skeleton className="h-[30px] w-[280px]" />}
        {shape === 'overview' ? (
          <>
            <div className="grid grid-cols-4 gap-npc-grid max-[900px]:grid-cols-2 max-[600px]:grid-cols-1">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-[96px]" />
              ))}
            </div>
            <Skeleton className="h-[220px]" />
          </>
        ) : null}
        {shape === 'list' ? <ListSkeletonBody rows={rows} stats={stats} /> : null}
        {shape === 'cards' ? <CardsSkeletonBody cols={cols} /> : null}
        {shape === 'timeline' ? <TimelineSkeletonBody /> : null}
        {shape === 'detail' ? <DetailSkeletonBody /> : null}
        {shape === 'chat' ? <ChatSkeletonBody /> : null}
        {shape === 'settings' ? <SettingsSkeletonBody /> : null}
        {shape === 'split' ? <SplitSkeletonBody /> : null}
      </div>
    </Page>
  )
}

/** 列表/表格页：可选统计条 + 一个带表头行的行面板（code、team、system-models、模型库）。 */
function ListSkeletonBody({ rows, stats }: { rows: number; stats: number }) {
  return (
    <>
      {stats > 0 ? (
        <div className="grid grid-cols-4 gap-npc-grid max-[900px]:grid-cols-2 max-[600px]:grid-cols-1">
          {Array.from({ length: stats }).map((_, i) => (
            <Skeleton key={i} className="h-[72px]" />
          ))}
        </div>
      ) : null}
      <div className="overflow-hidden rounded-npc-card border border-npc-border-soft bg-npc-panel">
        <div className="flex items-center gap-npc-grid-sm border-b border-npc-border-soft px-npc-grid-sm py-npc-gap-sm">
          {[0, 1, 2].map((i) => (
            <Skeleton
              key={i}
              className={cn(
                'h-[10px] rounded-[3px]' /* npc-token-allow: 骨架灰条 3px 小圆角，token 档位无此值 */,
                i === 0 ? 'w-[18%]' : 'w-[12%]',
              )}
            />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center gap-npc-grid-sm px-npc-grid-sm py-npc-row-y',
              i > 0 && 'border-t border-npc-border-soft',
            )}
          >
            <Skeleton className="size-[15px] shrink-0 rounded-npc-full" />
            <Skeleton
              className="h-[10px] w-[26%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            />{' '}
            <Skeleton
              className="h-[10px] w-[18%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            />{' '}
            <Skeleton className="ml-auto h-[10px] w-[54px] rounded-npc-chip" />
          </div>
        ))}
      </div>
    </>
  )
}

/** 卡片网格页：副标题条 + cols 列卡片（hosts、today、skills、vendor）。 */
function CardsSkeletonBody({ cols }: { cols: number }) {
  return (
    <>
      <Skeleton className="h-[16px] w-[360px] max-w-full" />
      <div
        className={cn(
          'grid gap-npc-card-lg',
          cols === 2 ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        )}
      >
        {Array.from({ length: cols === 2 ? 4 : 3 }).map((_, i) => (
          <div
            key={i}
            className="rounded-npc-card border border-npc-border-soft bg-npc-panel px-npc-card py-npc-grid-sm"
          >
            <Skeleton
              className="h-[13px] w-[38%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            />{' '}
            <div className="mt-npc-gap-sm flex flex-col gap-npc-gap-sm">
              <Skeleton
                className="h-[11px] w-[88%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
              />{' '}
              <Skeleton
                className="h-[11px] w-[64%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
              />{' '}
            </div>
            <div className="mt-npc-gap-md flex gap-npc-gap-xs">
              <Skeleton className="h-[18px] w-[64px] rounded-npc-chip" />
              <Skeleton className="h-[18px] w-[48px] rounded-npc-chip" />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

/** 时间线页：左轨圆点 + 卡片条目（timeline、activity）。 */
function TimelineSkeletonBody() {
  return (
    <div className="flex flex-col gap-npc-card-sm">
      <Skeleton className="h-[16px] w-[300px] max-w-full" />
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex items-stretch gap-npc-grid-sm">
          <div className="flex w-[14px] shrink-0 flex-col items-center pt-[14px]">
            <span className="size-[8px] shrink-0 rounded-npc-full bg-npc-surface animate-npc-pulse" />
            {i < 4 ? <span className="mt-[6px] w-px flex-1 bg-npc-border-soft" /> : null}
          </div>
          <div className="min-w-0 flex-1 rounded-npc-card border border-npc-border-soft bg-npc-panel px-npc-card py-npc-grid-sm">
            <div className="flex items-center gap-npc-gap-sm">
              <Skeleton
                className="h-[12px] w-[22%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
              />{' '}
              <Skeleton
                className="h-[10px] w-[12%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
              />{' '}
              <Skeleton className="ml-auto h-[16px] w-[56px] rounded-npc-chip" />
            </div>
            <div className="mt-npc-gap-sm flex flex-col gap-npc-gap-xs">
              <Skeleton
                className="h-[11px] w-[80%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
              />{' '}
              <Skeleton
                className="h-[11px] w-[52%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
              />{' '}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

/** 详情页：元信息条 + 双栏卡 + 底部宽区（server、任务详情、bridge、导入审阅）。 */
function DetailSkeletonBody() {
  return (
    <>
      <Skeleton className="h-[14px] w-[260px] max-w-full" />
      <div className="grid grid-cols-1 items-start gap-npc-card-sm lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
        <Skeleton className="h-[230px] rounded-npc-card" />
        <Skeleton className="h-[230px] rounded-npc-card" />
      </div>
      <Skeleton className="h-[130px] rounded-npc-card" />
    </>
  )
}

/** 聊天页：262px 话头列表 + 主对话区 + 326px 右栏（与 ChatView 同比例）。 */
function ChatSkeletonBody() {
  return (
    <div className="flex h-[calc(100vh-160px)] min-h-[420px] gap-npc-gap-md">
      <div className="flex w-[262px] shrink-0 flex-col gap-npc-gap-sm rounded-npc-card border border-npc-border-soft bg-npc-panel p-npc-grid-sm max-[600px]:hidden">
        {Array.from({ length: 9 }).map((_, i) => (
          <div key={i} className="flex flex-col gap-[5px]">
            <Skeleton
              className={cn(
                'h-[11px] rounded-[3px]' /* npc-token-allow: 骨架灰条 3px 小圆角，token 档位无此值 */,
                i % 3 === 0 ? 'w-[70%]' : 'w-[90%]',
              )}
            />{' '}
            <Skeleton
              className="h-[9px] w-[45%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            />{' '}
          </div>
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-end gap-npc-gap-sm">
        <div className="ml-auto max-w-[62%] rounded-npc-card bg-npc-surface px-npc-grid-sm py-npc-gap-sm">
          <Skeleton
            className="h-[11px] w-[180px] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
          />{' '}
        </div>
        <div className="max-w-[62%] rounded-npc-card border border-npc-border-soft bg-npc-panel px-npc-grid-sm py-npc-gap-sm">
          <Skeleton
            className="h-[11px] w-[240px] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
          />{' '}
          <Skeleton
            className="mt-[6px] h-[11px] w-[160px] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
          />{' '}
        </div>
        <div className="ml-auto max-w-[52%] rounded-npc-card bg-npc-surface px-npc-grid-sm py-npc-gap-sm">
          <Skeleton
            className="h-[11px] w-[130px] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
          />{' '}
        </div>
        <Skeleton className="mt-npc-gap-sm h-[52px] rounded-npc-card" />
      </div>
      <div className="hidden w-[326px] shrink-0 flex-col gap-npc-gap-sm rounded-npc-card border border-npc-border-soft bg-npc-panel p-npc-card xl:flex">
        {Array.from({ length: 7 }).map((_, i) => (
          <Skeleton
            key={i}
            className={cn(
              'h-[11px] rounded-[3px]' /* npc-token-allow: 骨架灰条 3px 小圆角，token 档位无此值 */,
              i % 3 === 2 ? 'w-[55%]' : 'w-full',
            )}
          />
        ))}
      </div>
    </div>
  )
}

/** 设置页：560px 窄栏叠卡，每卡「标签行 + 控件行」（account）。 */
function SettingsSkeletonBody() {
  return (
    <div className="flex max-w-[560px] flex-col gap-npc-card">
      {Array.from({ length: 3 }).map((_, i) => (
        <div
          key={i}
          className="rounded-npc-card border border-npc-border-soft bg-npc-panel px-npc-card py-npc-grid-sm"
        >
          <Skeleton
            className="h-[14px] w-[34%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
          />{' '}
          <div className="mt-npc-gap-md flex flex-col gap-npc-gap-sm">
            <div className="flex items-center justify-between gap-npc-grid-sm">
              <Skeleton
                className="h-[11px] w-[42%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
              />{' '}
              <Skeleton className="h-[26px] w-[64px] rounded-npc-chip" />
            </div>
            <Skeleton
              className="h-[11px] w-[70%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            />{' '}
          </div>
        </div>
      ))}
    </div>
  )
}

/** 双栏页：主列表 + 372px 侧栏（alert）。 */
function SplitSkeletonBody() {
  return (
    <div className="grid grid-cols-[1fr_372px] items-start gap-[26px] max-[1000px]:grid-cols-1">
      <div className="overflow-hidden rounded-npc-card border border-npc-border-soft bg-npc-panel">
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'flex items-center gap-npc-grid-sm px-npc-grid-sm py-npc-row-y',
              i > 0 && 'border-t border-npc-border-soft',
            )}
          >
            <Skeleton className="size-[15px] shrink-0 rounded-npc-full" />
            <Skeleton
              className="h-[10px] w-[34%] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            />{' '}
            <Skeleton
              className="ml-auto h-[10px] w-[72px] rounded-[3px]" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            />{' '}
          </div>
        ))}
      </div>
      <Skeleton className="h-[280px] rounded-npc-card" />
    </div>
  )
}

function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-npc-pulse rounded-npc-card bg-npc-surface', className)} />
}

/**
 * 出错了。
 *
 * 只显示 `user_message`——它是后端按 `docs/copy/` 模板渲染好的人话。
 * `code` 与 `developer_message` 永远不上屏（契约里写明了这一条）。
 *
 * ## 「这一块还没上」不长成一个错误
 *
 * 线上撞到的：`alert` 屏三条接口后端还没挂，于是**点进去是坏的，不是空的**。
 * 而这两件事对用户完全相反——没有数据是正常状态（往往还是好消息），
 * 接口不存在是**他做什么都没用**。
 *
 * 所以 `missing` 走中性底、不带琥珀、**不给任何行动点**。
 * 判据是 `kind`（`app/errors.ts` 分出来的），不是去翻那句话的字。
 *
 * > 当我们知道用户做什么都没用时，界面不给行动点，
 * > 比给一个安慰性的行动点诚实——一个点了没用的按钮，
 * > 会让他下次不再相信按钮。
 */
export function ScreenError({
  error,
  onRetry,
  title,
}: {
  error: unknown
  onRetry?: () => void
  title?: ReactNode
}) {
  const kind = error instanceof ApiFailure ? error.kind : 'reported'
  const missing = kind === 'missing'
  const forbidden = isForbiddenError(error)

  if (forbidden) {
    return (
      <Page>
        {title ? <PageHeader title={title} /> : null}
        <Card tone="plain" className="max-w-[560px]" data-testid="hosts-forbidden">
          <p className="m-0 text-npc-body-sm">{t('error.forbidden.hosts')}</p>
        </Card>
      </Page>
    )
  }

  // `missing` 不用后端那句，也不用 error.generic——**那句在说「我再试试」，
  // 而一条不存在的路由重试一万次还是不存在**
  const message = missing ? t('ui.not_ready') : t('error.generic')

  return (
    <Page>
      {title ? <PageHeader title={title} /> : null}
      <Card
        tone={missing ? 'plain' : 'amber'}
        className="max-w-[560px]"
        data-testid="hosts-read-failed"
      >
        {missing ? null : (
          <div className="mb-npc-gap-xs text-npc-body font-medium">
            {t('error.read_failed.title')}
          </div>
        )}
        <p className="m-0 text-npc-body-sm">{message}</p>
        {missing || !onRetry ? null : (
          <button
            type="button"
            className="mt-npc-gap-sm cursor-pointer border-none bg-transparent p-0 text-npc-caption text-npc-ink"
            onClick={onRetry}
          >
            {t('error.read_failed.retry')}
          </button>
        )}
      </Card>
    </Page>
  )
}

// ---------------------------------------------------------------- R4 三态标准

/**
 * 空态（R4，PRD FR-7）：讲这一页放什么（价值），不写「暂无数据」。
 * 与备忘录模块的空态同规矩——升格为全平台标准。
 */
export function EmptyState(props: {
  title: string
  desc?: string
  actions?: Array<{ label: string; onClick: () => void; primary?: boolean }>
  testId?: string
}) {
  return (
    <div
      data-testid={props.testId ?? 'empty-state'}
      className="rounded-npc-card border border-dashed border-npc-border-soft bg-npc-panel px-[18px] py-[26px] text-center"
    >
      <h3
        className="m-0 text-[14px] font-medium text-npc-ink-strong" /* npc-token-allow: 设计稿原值 */
      >
        {props.title}
      </h3>
      {props.desc ? (
        <p
          className="mx-auto mb-0 mt-[6px] max-w-[480px] text-[12.5px] leading-[1.6] text-npc-ink-soft" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
        >
          {' '}
          {props.desc}
        </p>
      ) : null}
      {props.actions && props.actions.length > 0 ? (
        <div className="mt-[13px] flex flex-wrap justify-center gap-[8px]">
          {props.actions.map((a) => (
            <button
              key={a.label}
              type="button"
              onClick={a.onClick}
              className={cn(
                'h-[32px] cursor-pointer rounded-[8px] border px-[14px] font-[inherit] text-[12.5px]' /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */,
                /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
                a.primary
                  ? 'border-none bg-m-pri text-m-pri-t'
                  : 'border-npc-border-soft bg-npc-panel text-npc-ink-medium hover:text-npc-ink-strong',
              )}
            >
              {a.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

/**
 * 失败态（R4，PRD FR-7）：回答三件事——出了什么事 · 东西在不在 · 该做什么。
 */
export function ErrorState(props: {
  what: string
  safeNote?: string
  retry?: () => void
  testId?: string
}) {
  return (
    <div
      role="alert"
      data-testid={props.testId ?? 'error-state'}
      className="rounded-npc-card border border-npc-red-border bg-npc-red-bg p-[14px]"
    >
      <p className="m-0 text-[13px] font-medium text-npc-red-ink" /* npc-token-allow: 设计稿原值 */>
        {props.what}
      </p>
      {props.safeNote ? (
        <p className="m-0 mt-[4px] text-[12px] text-npc-ink-soft" /* npc-token-allow: 设计稿原值 */>
          {props.safeNote}
        </p>
      ) : null}
      {props.retry ? (
        <button
          type="button"
          onClick={props.retry}
          className="mt-[8px] h-[28px] cursor-pointer rounded-[7px] border-none bg-m-pri px-[13px] font-[inherit] text-[12.5px] text-m-pri-t" /* npc-token-allow: 三态标准件按设计稿原值——灰条小圆角与字号，token 档位无此值 */
        >
          {t('error.read_failed.retry')}
        </button>
      ) : null}
    </div>
  )
}

/**
 * 行骨架（R4，PRD FR-7）：列表加载的行级骨架，「正在读取」写在头。
 */
export function RowSkeleton({ rows = 3, testId }: { rows?: number; testId?: string }) {
  return (
    <div
      data-testid={testId ?? 'row-skeleton'}
      className="rounded-npc-card border border-npc-border-soft bg-npc-panel"
    >
      <div className="flex items-center gap-[9px] border-b border-npc-border-soft px-[12px] py-[10px]">
        <span className="size-[7px] shrink-0 rounded-full bg-npc-ink-softer animate-npc-pulse" />
        <span className="text-[12px] text-npc-ink-softer" /* npc-token-allow: 设计稿原值 */>
          {t('nav.common.loading')}
        </span>
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-[12px] px-[12px] py-[13px]">
          <span className="size-[7px] shrink-0 rounded-full bg-npc-surface" />
          <span
            className="h-[10px] shrink-0 rounded-[3px] bg-npc-surface" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            style={{ width: `${20 + ((i * 15) % 25)}%` }}
          />
          <span
            className="h-[10px] flex-1 rounded-[3px] bg-npc-surface" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
            style={{ maxWidth: `${45 - (i % 3) * 8}%` }}
          />
          <span
            className="h-[10px] w-[40px] shrink-0 rounded-[3px] bg-npc-surface" /* npc-token-allow: 三态标准件/骨架按设计稿原值——灰条小圆角与字号，token 档位无此值 */
          />{' '}
        </div>
      ))}
    </div>
  )
}
